import { fetchMobileVersion } from "../api/mobileVersion";
import { getNativeVersionInfo, isNative } from "./mobile";

const DEFAULT_MESSAGE =
  "يتوفر تحديث جديد للتطبيق. يرجى التحديث للمتابعة.";

/**
 * Compare installed Android versionCode with server latest.
 * Fail-open on errors / non-native / missing server config.
 *
 * @returns {Promise<{
 *   updateRequired: boolean,
 *   localVersion: string,
 *   localVersionCode: number,
 *   latestVersion: string,
 *   latestVersionCode: number,
 *   apkUrl: string,
 *   message: string,
 * }>}
 */
export async function checkAppUpdate() {
  const empty = {
    updateRequired: false,
    localVersion: "",
    localVersionCode: 0,
    latestVersion: "",
    latestVersionCode: 0,
    apkUrl: "",
    message: DEFAULT_MESSAGE,
  };

  if (!isNative()) {
    return empty;
  }

  try {
    const [nativeInfo, remote] = await Promise.all([
      getNativeVersionInfo(),
      fetchMobileVersion(),
    ]);

    const localVersionCode = parseInt(String(nativeInfo.build || "0"), 10) || 0;
    const latestVersionCode =
      parseInt(String(remote?.latestVersionCode ?? 0), 10) || 0;
    const apkUrl = (remote?.apkUrl || "").trim();
    const latestVersion = remote?.latestVersion || "";
    const message = remote?.message || DEFAULT_MESSAGE;

    // Fail-open if the native versionCode cannot be read (0). Blocking here
    // traps the user in an install loop even after they already updated.
    const updateRequired =
      Boolean(apkUrl) &&
      latestVersionCode > 0 &&
      localVersionCode > 0 &&
      localVersionCode < latestVersionCode;

    return {
      updateRequired,
      localVersion: nativeInfo.version || "",
      localVersionCode,
      latestVersion,
      latestVersionCode,
      apkUrl,
      message,
    };
  } catch {
    // Network / API failure — do not block the app
    return empty;
  }
}

/** Open the APK download URL (browser / system handler). */
export function openApkDownload(apkUrl) {
  if (!apkUrl) return;
  try {
    window.open(apkUrl, "_blank", "noopener,noreferrer");
  } catch {
    const anchor = document.createElement("a");
    anchor.href = apkUrl;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
}
